import { TravelMode } from '../types';
import { addDays, dateRange, MODE_LABELS } from './plan';
import { sameTown as same } from './towns';
import type { BookedStay, FixedPoint } from './plan';

// A booking, read door to door.
//
// plan-trip's parse_booking only extracts: every leg as printed, and any
// stays. What those legs mean for the trip is worked out here, deterministically,
// so the same booking always gives the same outline:
//   - the trip starts when the first leg leaves and, on a round trip, finishes
//     when the last one lands back where it began
//   - two legs that meet at the same place within LAYOVER_MAX_HOURS make a
//     layover — time somewhere, worth a meal or a lounge, not a night
//   - a longer gap is a stay: that night is pinned to the town
//   - a night that falls between a leg leaving and arriving is spent travelling

/** A connection longer than this is a stay, not a layover. */
export const LAYOVER_MAX_HOURS = 12;

export interface BookingLeg {
  date: string;
  depart_time: string | null;
  from: string;
  from_code: string | null;
  arrive_date: string | null;
  arrive_time: string | null;
  to: string;
  to_code: string | null;
  mode: TravelMode | null;
  number: string | null;
  confirmation: string | null;
}

export interface BookingResult {
  destination: string | null;
  legs: BookingLeg[];
  stays: BookedStay[];
  notes: string | null;
}

export interface OutlineEnd {
  date: string;
  time: string | null;
  place: string;
  mode: TravelMode | null;
  detail: string | null;
}

export interface Layover {
  place: string;
  /** Airport or station, as the booking shows it. */
  code: string | null;
  date: string;
  from: string | null;
  until: string | null;
  /** Null when either time is missing. */
  hours: number | null;
}

export interface TransitNight {
  date: string;
  /** "Overnight flight · CX 881" */
  lodging: string;
}

export interface TripOutline {
  start: OutlineEnd | null;
  /** Only on a round trip; a one-way booking leaves the finish to the traveller. */
  end: OutlineEnd | null;
  legs: BookingLeg[];
  layovers: Layover[];
  /** Arrivals you sleep after: each pins its first night to the town. */
  arrivals: FixedPoint[];
  transitNights: TransitNight[];
}

/** "CX 881 · LAX → HKG · conf BOHMOF" */
export function legDetail(l: BookingLeg): string | null {
  const route = l.from_code && l.to_code ? `${l.from_code} → ${l.to_code}` : l.from_code ?? l.to_code;
  return [l.number, route, l.confirmation ? `conf ${l.confirmation}` : null].filter(Boolean).join(' · ') || null;
}


function minutes(t: string): number {
  return Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
}

/** Hours from one local date+time to another at the same place (one time zone). */
export function gapHours(fromDate: string, fromTime: string, toDate: string, toTime: string): number {
  const days = (Date.parse(`${toDate}T12:00:00Z`) - Date.parse(`${fromDate}T12:00:00Z`)) / 86_400_000;
  return Math.round(((days * 1440 + minutes(toTime) - minutes(fromTime)) / 60) * 10) / 10;
}

function overnight(mode: TravelMode | null): string {
  if (mode === 'flight') return 'Overnight flight';
  if (mode === 'train') return 'Overnight train';
  if (mode === 'ferry') return 'Overnight ferry';
  if (mode === 'bus') return 'Overnight bus';
  return 'Overnight travel';
}

export function tripOutline(legs: BookingLeg[]): TripOutline {
  const out: TripOutline = { start: null, end: null, legs, layovers: [], arrivals: [], transitNights: [] };
  if (!legs.length) return out;

  const first = legs[0];
  const last = legs[legs.length - 1];
  const home = first.from;
  out.start = { date: first.date, time: first.depart_time, place: first.from, mode: first.mode, detail: legDetail(first) };
  if (same(last.to, home)) {
    out.end = {
      date: last.arrive_date ?? last.date,
      time: last.arrive_time,
      place: last.to,
      mode: last.mode,
      detail: legDetail(last),
    };
  }

  const transit = new Map<string, string>();
  legs.forEach((l, i) => {
    const arrive = l.arrive_date ?? l.date;
    // Every night between leaving and arriving is spent on board.
    if (arrive > l.date) {
      const what = [overnight(l.mode), l.number].filter(Boolean).join(' · ');
      for (const d of dateRange(l.date, addDays(arrive, -1))) transit.set(d, what);
    }

    const next = legs[i + 1];
    if (same(l.to, home) && !next) return;
    const hours =
      next && l.arrive_time && next.depart_time ? gapHours(arrive, l.arrive_time, next.date, next.depart_time) : null;
    // Without both times, a connection the same day reads as a layover.
    const layover =
      !!next && same(l.to, next.from) && (hours !== null ? hours >= 0 && hours < LAYOVER_MAX_HOURS : next.date === arrive);
    if (layover) {
      out.layovers.push({ place: l.to, code: l.to_code, date: arrive, from: l.arrive_time, until: next!.depart_time, hours });
      for (const d of next!.date > arrive ? dateRange(arrive, addDays(next!.date, -1)) : []) {
        transit.set(d, `Overnight layover · ${l.to}`);
      }
    } else if (!same(l.to, home)) {
      out.arrivals.push({ town: l.to, date: arrive, mode: l.mode, detail: legDetail(l), from: l.from, booked: true });
    }
  });

  out.transitNights = Array.from(transit, ([date, lodging]) => ({ date, lodging })).sort((a, b) => (a.date < b.date ? -1 : 1));
  return out;
}

/** "3 h 30 m" */
export function formatHours(h: number): string {
  const whole = Math.floor(h);
  const mins = Math.round((h - whole) * 60);
  return [whole ? `${whole} h` : null, mins ? `${mins} m` : null].filter(Boolean).join(' ') || '0 m';
}

export function modeLabel(mode: TravelMode | null): string {
  return mode ? MODE_LABELS[mode] : 'How not stated';
}
